import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { renderHighlightedJSON } from './atUtils';

const TYPE_ABBR = {
  string: 'string',
  integer: 'integer',
  number: 'number',
  boolean: 'boolean',
  object: 'object',
  array: 'array',
  null: 'null',
};

function inferType(schema) {
  if (Array.isArray(schema.type)) return schema.type.join('|');
  if (schema.type) return schema.type;
  if (schema.properties) return 'object';
  return schema.enum ? 'enum' : 'any';
}

function SchemaProp({ name, schema, required = false }) {
  const type = inferType(schema);
  const desc = schema.description;
  const isObject = !!schema.properties;
  const isArray = type === 'array' && schema.items;
  const childRequired = schema.required || [];

  return (
    <>
      <div className="schema-prop">
        <span className="schema-prop-key">
          {name}
          {required && (
            <span className="required-star" title="Required">
              *
            </span>
          )}
        </span>
        <span className="schema-prop-type">{TYPE_ABBR[type] || type}</span>
        {desc && <div className="schema-prop-desc">{desc}</div>}
      </div>
      {isObject && (
        <div className="schema-children">
          {Object.entries(schema.properties).map(([k, sub]) => (
            <SchemaProp
              key={k}
              name={k}
              schema={sub}
              required={childRequired.includes(k)}
            />
          ))}
        </div>
      )}
      {isArray && (
        <div className="schema-children">
          {schema.items.type === 'object' && schema.items.properties ? (
            Object.entries(schema.items.properties).map(([k, sub]) => (
              <SchemaProp
                key={k}
                name={`[].${k}`}
                schema={sub}
                required={(schema.items.required || []).includes(k)}
              />
            ))
          ) : (
            <div className="schema-prop">
              <span className="schema-prop-key muted">[]</span>
              <span className="schema-prop-type">
                {schema.items.type || 'any'}
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
SchemaProp.propTypes = {
  name: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object.isRequired,
  required: PropTypes.bool,
};

export default function SchemaViewer({ label, schema = {} }) {
  const [mode, setMode] = useState('pretty');

  const rootProps = schema?.properties ?? {};
  const rootRequired = schema?.required ?? [];

  return (
    <div className="schema-block">
      <div className="schema-head">
        <span className="label">{label}</span>
        <div className="schema-toggle">
          <button
            type="button"
            className={mode === 'pretty' ? 'active' : ''}
            onClick={() => setMode('pretty')}
          >
            Pretty
          </button>
          <button
            type="button"
            className={mode === 'raw' ? 'active' : ''}
            onClick={() => setMode('raw')}
          >
            Raw JSON
          </button>
        </div>
      </div>
      {mode === 'pretty' && (
        <div className="schema-note">
          Simplified view — see Raw JSON for the full schema.
        </div>
      )}
      {mode === 'pretty' ? (
        <div className="schema-body">
          {Object.keys(rootProps).length === 0 ? (
            <div className="schema-prop muted">No properties defined.</div>
          ) : (
            Object.entries(rootProps).map(([k, sub]) => (
              <SchemaProp
                key={k}
                name={k}
                schema={sub}
                required={rootRequired.includes(k)}
              />
            ))
          )}
        </div>
      ) : (
        <pre
          className="schema-raw"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: renderHighlightedJSON(schema) }}
        />
      )}
    </div>
  );
}

SchemaViewer.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object,
};
